import { Button, CampoDeTexto, cn } from "@base/ui";
import { AlertTriangle, KeyRound, Link2, LoaderCircle } from "lucide-react";
import { useState, type FormEvent } from "react";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useConectarKommo } from "../application/conexoes.contexto";

const PASSOS = [
  "No Kommo, abra Configurações › Integrações e crie uma integração privada.",
  "Na aba Chaves e escopos, gere um token de longa duração com a validade mais longa.",
  "Cole abaixo o endereço da conta e o token. Ele é testado na hora e guardado cifrado.",
] as const;

/** Endereco da conta e token de longa duracao: conectar ou trocar o token. */
export function FormularioDeConexao({
  clienteId,
  subdominioAtual,
  aoConcluir,
  aoCancelar,
  className,
}: {
  readonly clienteId: string;
  readonly subdominioAtual: string | null;
  readonly aoConcluir: () => void;
  readonly aoCancelar?: () => void;
  readonly className?: string;
}) {
  const [subdominio, setSubdominio] = useState(subdominioAtual ? `${subdominioAtual}.kommo.com` : "");
  const [token, setToken] = useState("");
  const conectar = useConectarKommo(clienteId);
  const campos = conectar.error instanceof ErroDaApi ? conectar.error.campos : {};

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    conectar.mutate(
      { subdominio, token },
      {
        onSuccess: () => {
          // O token nao fica nem no estado da tela depois de enviado.
          setToken("");
          aoConcluir();
        },
      },
    );
  }

  return (
    <form onSubmit={enviar} className={cn("flex flex-col gap-bloco", className)} noValidate>
      <ol className="flex flex-col gap-4">
        {PASSOS.map((passo, indice) => (
          <li key={passo} className="flex items-start gap-3 text-sm leading-relaxed">
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
              {indice + 1}
            </span>
            {passo}
          </li>
        ))}
      </ol>

      <CampoDeTexto
        rotulo="Endereço da conta"
        placeholder="suaclinica.kommo.com"
        autoComplete="off"
        value={subdominio}
        onChange={(evento) => setSubdominio(evento.target.value)}
        icone={<Link2 className="size-4" aria-hidden="true" />}
        erros={campos.subdominio}
        required
      />
      <CampoDeTexto
        rotulo="Token de longa duração"
        type="password"
        autoComplete="off"
        spellCheck={false}
        value={token}
        onChange={(evento) => setToken(evento.target.value)}
        icone={<KeyRound className="size-4" aria-hidden="true" />}
        erros={campos.token}
        dica="Só a Monte Saúde usa o token, e ele nunca volta a aparecer na tela."
        required
      />

      {conectar.isError && Object.keys(campos).length === 0 ? (
        <p role="alert" className="flex items-start gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {mensagemDoErro(conectar.error)}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        <Button type="submit" disabled={conectar.isPending || !subdominio.trim() || !token.trim()}>
          {conectar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          {aoCancelar ? "Salvar novo token" : "Conectar Kommo CRM"}
        </Button>
        {aoCancelar ? (
          <Button type="button" variant="ghost" onClick={aoCancelar}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}
